import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import SeoHead from "../components/SeoHead";
import InboxPanel from "../components/InboxPanel";
import SubscriptionExpiredModal from "../components/SubscriptionExpiredModal";
import UserApiAccessSection from "../components/UserApiAccessSection";
import { formatInboxEmail } from "../utils/inbox";
import {
  formatDaysRemaining,
  getDaysRemaining,
  isPremiumUser,
  PREMIUM_API_MESSAGE,
  userHasAccess,
} from "../utils/plan";
import { useAuth } from "../context/AuthContext";

const USER_MENU_SECTIONS = [
  { id: "profile", label: "My Profile" },
  { id: "billing", label: "Billing" },
  { id: "api", label: "API Access", premium: true },
];

function UserProfileSection({ user, updateProfile }) {
  const [name, setName] = useState(user?.name || "");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    setName(user?.name || "");
  }, [user?.name]);

  const handleSave = async (e) => {
    e.preventDefault();
    setMessage("");
    setIsError(false);
    setSaving(true);

    const result = await updateProfile({ name: name.trim() });
    setSaving(false);

    if (!result.ok) {
      setIsError(true);
      setMessage(result.error);
      return;
    }

    setEditing(false);
    setMessage("Profile updated successfully.");
  };

  const handleCancel = () => {
    setName(user?.name || "");
    setEditing(false);
    setMessage("");
    setIsError(false);
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white">My Profile</h2>
        <p className="mt-1 text-sm text-zinc-400">View and update your account details.</p>
      </div>

      <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-6">
        <dl className="space-y-5">
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-zinc-500">Email</dt>
            <dd className="mt-1 font-mono text-sm text-zinc-200">{user?.email}</dd>
          </div>

          {user?.inboxSubdomain && (
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                Inbox subdomain
              </dt>
              <dd className="mt-1 font-mono text-sm text-emerald-400">.{user.inboxSubdomain}</dd>
            </div>
          )}

          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-zinc-500">
              Display name
            </dt>
            {!editing ? (
              <dd className="mt-2 flex flex-wrap items-center gap-3">
                <span className="text-lg text-white">{user?.name}</span>
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="rounded-lg border border-zinc-700 px-3 py-1.5 text-xs text-zinc-300 hover:border-zinc-500 hover:text-white"
                >
                  Edit name
                </button>
              </dd>
            ) : (
              <dd className="mt-2">
                <form onSubmit={handleSave} className="max-w-md space-y-3">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    maxLength={100}
                    className="w-full rounded-lg border border-zinc-700 bg-zinc-950 px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none"
                  />
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={saving || !name.trim()}
                      className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-zinc-950 hover:bg-emerald-400 disabled:opacity-60"
                    >
                      {saving ? "Saving…" : "Save"}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancel}
                      disabled={saving}
                      className="rounded-lg border border-zinc-700 px-4 py-2 text-sm text-zinc-300 hover:border-zinc-500"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </dd>
            )}
          </div>
        </dl>

        {message && (
          <p className={`mt-4 text-sm ${isError ? "text-red-400" : "text-emerald-400"}`}>
            {message}
          </p>
        )}
      </div>
    </div>
  );
}

function UserBillingSection({ user, isExpired }) {
  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white">Billing</h2>
        <p className="mt-1 text-sm text-zinc-400">
          Manage your subscription and payment methods.
        </p>
      </div>

      <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
              Current plan
            </p>
            <p className="mt-1 text-lg font-semibold text-white">
              {user?.plan === "pro" ? "Pro" : "Free trial"}
            </p>
            {!isExpired && (
              <p className="mt-1 text-sm text-emerald-400">
                {formatDaysRemaining(getDaysRemaining(user?.planExpiresAt))}
              </p>
            )}
            {isExpired && (
              <p className="mt-1 text-sm text-amber-400">Your trial has expired</p>
            )}
          </div>
        </div>

        <div className="mt-6 rounded-lg border border-dashed border-zinc-700 bg-zinc-950/40 p-5 text-center">
          <p className="text-sm font-medium text-zinc-300">
            {isExpired ? "Subscribe to Pro to continue" : "Upgrade to Pro"}
          </p>
          <p className="mt-2 text-xs leading-relaxed text-zinc-500">
            Online subscription and payment will be available soon. You will be able to upgrade
            securely from this page.
          </p>
          <button
            type="button"
            disabled
            className="mt-4 rounded-lg bg-emerald-500/40 px-6 py-2.5 text-sm font-semibold text-zinc-400 cursor-not-allowed"
          >
            Coming soon
          </button>
        </div>
      </div>
    </div>
  );
}

function InboxDashboard() {
  const {
    user,
    saveActiveInbox,
    updateProfile,
    syncUser,
    impersonating,
    exitImpersonation,
    getAuthHeaders,
    isAdmin,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const sectionParam = searchParams.get("section");
  const activeSection =
    sectionParam === "profile" ||
    sectionParam === "billing" ||
    sectionParam === "api"
      ? sectionParam
      : "inbox";

  const inboxFromUrl = new URLSearchParams(location.search).get("inbox") || "";
  const isAdminUser = isAdmin && !impersonating;
  const isExpired = user?.role !== "admin" && (user?.isExpired || !userHasAccess(user));
  const isPremium = isPremiumUser(user);
  const [, setTick] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [snack, setSnack] = useState(null);
  const snackTimeoutRef = useRef(null);
  const daysRemaining = getDaysRemaining(user?.planExpiresAt);

  const showSnack = useCallback((text, isError = false) => {
    if (snackTimeoutRef.current) clearTimeout(snackTimeoutRef.current);
    setSnack({ text, isError });
    snackTimeoutRef.current = setTimeout(() => {
      setSnack(null);
      snackTimeoutRef.current = null;
    }, 3500);
  }, []);

  useEffect(() => {
    return () => {
      if (snackTimeoutRef.current) clearTimeout(snackTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (isExpired && activeSection === "inbox") {
      setSearchParams({ section: "billing" }, { replace: true });
    }
  }, [isExpired, activeSection, setSearchParams]);

  const setActiveSection = (sectionId) => {
    if (sectionId === "api" && !isPremium) {
      showSnack(PREMIUM_API_MESSAGE, true);
      setSidebarOpen(false);
      return;
    }

    if (isExpired && sectionId === "inbox") {
      setSearchParams({ section: "billing" }, { replace: true });
      setSidebarOpen(false);
      return;
    }
    if (sectionId === "inbox") {
      const params = new URLSearchParams();
      if (inboxFromUrl) params.set("inbox", inboxFromUrl);
      setSearchParams(params, { replace: true });
      setSidebarOpen(false);
      return;
    }
    setSearchParams({ section: sectionId }, { replace: true });
    setSidebarOpen(false);
  };

  const sectionLabels = {
    inbox: "Inbox",
    profile: "My Profile",
    billing: "Billing",
    api: "API Access",
  };

  useEffect(() => {
    if (activeSection === "api" && !isPremium) {
      setSearchParams({ section: "billing" }, { replace: true });
      showSnack(PREMIUM_API_MESSAGE, true);
    }
  }, [activeSection, isPremium, setSearchParams, showSnack]);

  const handleExitImpersonation = async () => {
    const result = await exitImpersonation();
    if (result.ok) {
      navigate("/admin");
    }
  };

  const renderInbox = () => {
    if (isExpired) {
      return (
        <div className="flex min-h-[240px] flex-col items-center justify-center text-center">
          <p className="text-sm font-medium text-zinc-300">Your free trial has ended</p>
          <p className="mt-2 max-w-sm text-xs text-zinc-500">
            Subscribe to Pro to continue using your inbox.
          </p>
          <button
            type="button"
            onClick={() => setActiveSection("billing")}
            className="mt-4 rounded-lg bg-emerald-500 px-6 py-2.5 text-sm font-semibold text-zinc-950 hover:bg-emerald-400"
          >
            Go to billing
          </button>
        </div>
      );
    }

    return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white">Inbox</h2>
        <p className="mt-1 text-sm text-zinc-400">
          Create a temporary inbox address and receive mail in real time.
        </p>
      </div>

      <InboxPanel
        user={user}
        isAdminUser={isAdminUser}
        getAuthHeaders={getAuthHeaders}
        saveActiveInbox={saveActiveInbox}
        initialInboxName={inboxFromUrl}
        autoLoad={Boolean(inboxFromUrl)}
      />

      <div className="mt-10">
        <h3 className="text-center text-lg font-semibold text-white">
          Popular Testing Scenarios
        </h3>
        <div className="mt-4 grid grid-cols-2 gap-2 text-center text-sm md:grid-cols-4">
          {[
            "facebook", "instagram", "amazon", "discord", "netflix", "spotify",
            "linkedin", "github", "tiktok", "reddit", "steam", "flipkart",
          ].map((kw) => (
            <Link
              key={kw}
              to={`/temp-email-for/${kw}`}
              className="capitalize text-emerald-400/90 hover:text-emerald-300 hover:underline"
            >
              {kw.replace(/-/g, " ")}
            </Link>
          ))}
        </div>
      </div>
    </div>
    );
  };

  const renderSection = () => {
    switch (activeSection) {
      case "profile":
        return <UserProfileSection user={user} updateProfile={updateProfile} />;
      case "billing":
        return <UserBillingSection user={user} isExpired={isExpired} />;
      case "api":
        return (
          <UserApiAccessSection
            user={user}
            getAuthHeaders={getAuthHeaders}
            onUserUpdate={syncUser}
          />
        );
      default:
        return renderInbox();
    }
  };

  return (
    <Layout>
      <SeoHead title="Dashboard – Ghost Inbox" noindex />

      {isExpired && !impersonating && (
        <SubscriptionExpiredModal onGoToBilling={() => setActiveSection("billing")} />
      )}

      <div className="relative mx-auto flex max-w-[100rem] gap-0 px-3 py-6 sm:px-4 sm:py-8 lg:gap-6 lg:px-6 lg:py-10">
        {sidebarOpen && (
          <button
            type="button"
            className="fixed inset-0 z-30 bg-black/60 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
          />
        )}

        <aside
          className={`fixed inset-y-0 left-0 z-40 w-72 max-w-[min(100vw,20rem)] shrink-0 transform overflow-y-auto border-r border-zinc-800 bg-zinc-950 p-4 transition-all duration-200 ease-out lg:static lg:z-auto lg:max-w-none lg:overflow-visible lg:border-r-0 lg:bg-transparent lg:p-0 ${
            sidebarOpen
              ? "translate-x-0 lg:w-56"
              : "-translate-x-full lg:w-0 lg:overflow-hidden lg:p-0 lg:opacity-0 lg:pointer-events-none"
          }`}
          aria-hidden={!sidebarOpen}
        >
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-4 lg:sticky lg:top-6">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium uppercase tracking-wider text-emerald-400">
                  Account
                </p>
                <p className="mt-1 truncate text-sm text-zinc-400">{user?.email}</p>
              </div>
              <button
                type="button"
                onClick={() => setSidebarOpen(false)}
                className="shrink-0 rounded-lg border border-zinc-700 p-2 text-zinc-400 hover:border-zinc-500 hover:text-white"
                aria-label="Close menu"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            </div>

            {user?.role !== "admin" && !isExpired && (
              <div className="mt-4 rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-3 py-2">
                <p className="text-xs font-medium text-emerald-400">
                  {user?.plan === "pro" ? "Pro plan" : "Free trial"}
                </p>
                <p className="mt-0.5 text-sm font-semibold text-white">
                  {formatDaysRemaining(daysRemaining)}
                </p>
              </div>
            )}

            <nav className="mt-6 space-y-1">
              <button
                type="button"
                onClick={() => setActiveSection("inbox")}
                disabled={isExpired}
                className={`flex w-full rounded-lg px-3 py-2.5 text-left text-sm font-medium transition ${
                  activeSection === "inbox"
                    ? "bg-emerald-500/15 text-emerald-400"
                    : isExpired
                      ? "cursor-not-allowed text-zinc-600"
                      : "text-zinc-300 hover:bg-zinc-800/60 hover:text-white"
                }`}
              >
                Inbox
              </button>
              {USER_MENU_SECTIONS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveSection(item.id)}
                  className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm font-medium transition ${
                    activeSection === item.id
                      ? "bg-emerald-500/15 text-emerald-400"
                      : "text-zinc-300 hover:bg-zinc-800/60 hover:text-white"
                  }`}
                >
                  <span>{item.label}</span>
                  {item.premium && !isPremium && (
                    <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-400">
                      Pro
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        <div className="min-w-0 flex-1 lg:min-w-0">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className={`flex items-center gap-2 rounded-lg border border-zinc-700 bg-zinc-900/60 px-3 py-2 text-sm font-medium text-zinc-200 transition hover:border-zinc-500 hover:text-white ${
                sidebarOpen ? "lg:hidden" : ""
              }`}
              aria-expanded={sidebarOpen}
              aria-label="Open menu"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
              Menu
            </button>
            <span className="text-sm text-zinc-500">
              {sectionLabels[activeSection] || "Dashboard"}
            </span>
          </div>
          {impersonating && (
            <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-sky-800/60 bg-sky-950/30 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-sky-200">
                Viewing as <span className="font-medium text-white">{user?.email}</span>
                {user?.activeInbox ? (
                  <>
                    {" "}
                    · Inbox{" "}
                    <span className="font-mono text-emerald-300">
                      {formatInboxEmail(
                        user.activeInbox,
                        user.inboxSubdomain,
                        user.role === "admin",
                      )}
                    </span>
                  </>
                ) : null}
              </p>
              <button
                type="button"
                onClick={handleExitImpersonation}
                className="self-start rounded-lg border border-sky-600 px-4 py-2 text-sm text-sky-200 hover:border-sky-400"
              >
                Back to admin
              </button>
            </div>
          )}

          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/30 p-4 sm:p-6">
            {snack && (
              <div
                role="status"
                className={`mb-4 rounded-lg border px-4 py-3 text-sm ${
                  snack.isError
                    ? "border-red-800/60 bg-red-950/40 text-red-300"
                    : "border-emerald-800/60 bg-emerald-950/40 text-emerald-300"
                }`}
              >
                {snack.text}
              </div>
            )}
            {renderSection()}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default InboxDashboard;
